import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { array, string } from 'prop-types'
import { measure } from 'remeasure'

// Import Components
import { TabBody } from './TabBody'
import { TabArrow } from './Tabs/TabArrow'

const BestPicksContainer = styled.div`
	box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
	max-width: 975px;
	width: 100%;
`

const TabsWrapper = styled.div`
	box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
	overflow-x: auto;
	white-space: nowrap;
	width: 100%;
	z-index: -1;

	::-webkit-scrollbar {
		display: none;
	}
`

const TabsContainer = styled.div`
	padding-top: 5px;
	transition: left 0.15s ease;
	display: table;
	margin-left: 32px;
	margin-right: 32px;

	${({ theme }) => theme.MQ.M} {
		${({ displayArrows }) => (displayArrows ? `margin: 0 30px` : `margin: 0;`)};
		padding-bottom: 2px;
	}
`
const Tab = styled.div`
	background-color: transparent;
	display: inline;
	height: 32px;
	margin-left: 0;
	margin-right: ${({ isLastTab }) => isLastTab && '32px'};
`
const TabLabel = styled.div`
	border-bottom-style: solid;
	border-color: ${({ isActiveTab, theme }) => (isActiveTab ? theme.colors.orange : 'transparent')};
	border-width: 0.3125rem;
	color: ${({ isActiveTab, theme }) => (isActiveTab ? theme.colors.text : theme.colors.darkGrey)};
	display: inline-block;
	font-size: 16px;
	font-weight: 700;
	margin: 0 5px;
	padding: 0 20px 5px 20px;
	text-transform: capitalize;
	transition: 0.2s ease border-color;

	${({ theme }) => theme.MQ.S} {
		font-size: 14px;
	}

	&:hover {
		border-color: ${({ isActiveTab, theme }) => !isActiveTab && theme.colors.teal};
		color: ${({ theme }) => theme.colors.text};
		cursor: pointer;
	}
`

const propTypes = {
	categoryWidgetId: string,
	services: array.isRequired,
	servicesData: array.isRequired,
}

const measureWithKeysAndOptions = measure(['clientWidth', 'scrollWidth'], { flatten: true })

export const BestPicks = measureWithKeysAndOptions((props) => {
	const { categoryWidgetId, bestpicks } = props
	const [currentTab, setCurrentTab] = useState(0)
	const [currentTabData, setCurrentTabData] = useState({ ...bestpicks[0], id: 0 })
	const [tabData, setTabData] = useState([])

	const [displayArrows, setDisplayArrows] = useState(null)
	const tabContainer = useRef(null)
	const clientWidth = tabContainer.current && tabContainer.current.clientWidth
	const scrollWidth = tabContainer.current && tabContainer.current.scrollWidth - 64 // account for arrow widths

	const handleResize = () => {
		const clientWidth = tabContainer.current.clientWidth
		const scrollWidth = tabContainer.current.scrollWidth - 64 // account for arrow widths
		setDisplayArrows(clientWidth < scrollWidth)
	}

	// display arrows based on scrollWidth vs clientWidth
	useEffect(() => {
		setDisplayArrows(clientWidth < scrollWidth)

		window.addEventListener('resize', handleResize)

		return () => {
			window.removeEventListener('resize', handleResize)
		}
	}, [clientWidth, scrollWidth])

	useEffect(() => {
		const bestPicksArray = bestpicks.map((bestpick, i) => {
			return {
				label: bestpick.tabTitle,
				...bestpick,
				id: i,
			}
		})
		setTabData(bestPicksArray)
		setCurrentTabData(bestPicksArray[0])
	}, []) //eslint-disable-line

	const handleTabChange = (tab) => {
		setCurrentTab(tab)
	}

	useEffect(() => {
		if (tabData) {
			setCurrentTabData(tabData[currentTab])
		}
	}, [currentTab, tabData])

	// click handler for right arrow
	const handleNextClick = (e) => {
		const tabsContainer = e.currentTarget.parentNode
		const newTabPosition = 70 + tabsContainer.scrollLeft
		tabsContainer.scrollLeft = newTabPosition
	}

	// click handler for left arrow
	const handlePrevClick = (e) => {
		const tabsContainer = e.currentTarget.parentNode
		const newTabPosition = tabsContainer.scrollLeft - 70
		tabsContainer.scrollLeft = newTabPosition
	}

	return (
		<BestPicksContainer>
			<TabsWrapper ref={tabContainer}>
				<TabArrow direction="left" displayArrow={displayArrows} onClick={handlePrevClick} />
				<TabArrow direction="right" displayArrow={displayArrows} onClick={handleNextClick} />
				<TabsContainer displayArrows={displayArrows}>
					{tabData &&
						tabData.map((tab) => (
							<Tab key={tab.id} onClick={() => handleTabChange(tab.id)}>
								<TabLabel isActiveTab={currentTab === tab.id}>{tab.label}</TabLabel>
							</Tab>
						))}
				</TabsContainer>
			</TabsWrapper>
			{currentTabData !== undefined && (
				<TabBody categoryWidgetId={categoryWidgetId} currentTab={currentTab} {...currentTabData} />
			)}
		</BestPicksContainer>
	)
})

BestPicks.propTypes = propTypes
